import { useTranslation } from 'react-i18next';
import { KeyRound, LogOut } from 'lucide-react';
import { useAuth } from '../auth/AuthProvider';
import { PasswordChangeForm } from '../components/settings/PasswordChangeForm';

/**
 * The only screen a user with `mustChangePassword` may reach (decision D8).
 *
 * Rendered outside `AppShell`, deliberately: the bottom navigation would offer four
 * places none of which this session can load, and a tab bar whose every destination
 * answers 403 is worse than no tab bar.
 *
 * Sign-out stays, and it is not decoration. Somebody who cannot complete the change —
 * they have forgotten the temporary password already, they are on the wrong account,
 * the handset is not theirs — must have a way out. Without it the app is a trap: an
 * account that can neither be used nor left.
 *
 * The wording says what happened rather than what they did wrong. An admin issued this
 * password, and the person reading it has not made a mistake.
 */
export function ForcedPasswordChangePage() {
  const { t } = useTranslation();
  const { user, signOut } = useAuth();

  return (
    <main className="flex min-h-dvh flex-col justify-center bg-slate-950 px-6 py-10 text-slate-100">
      <div className="mx-auto w-full max-w-sm space-y-5">
        <div className="rounded-2xl border-b-4 border-brookfields-gold bg-brookfields-green px-6 py-6 text-center">
          <KeyRound className="mx-auto size-6 text-brookfields-gold-light" aria-hidden="true" />
          <h1 className="mt-2 text-lg font-semibold tracking-tight text-white">
            {t('forcedChange.heading')}
          </h1>
          <p className="mt-1 text-sm text-brookfields-gold-light">{t('forcedChange.explain')}</p>
        </div>

        {user && <p className="text-center text-xs text-slate-400">{user.email}</p>}

        <PasswordChangeForm />

        <button
          type="button"
          onClick={() => void signOut()}
          className="flex min-h-[48px] w-full items-center justify-center gap-2 rounded-lg bg-transparent text-sm text-slate-400"
        >
          <LogOut className="size-4" aria-hidden="true" />
          {t('forcedChange.signOut')}
        </button>
      </div>
    </main>
  );
}
